import React from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Avatar,
  Grid,
  Paper,
  IconButton,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

const WhatsAppSettingsPanel = ({ 
  contactName, 
  setContactName, 
  contactAvatar, 
  setContactAvatar,
  handleAvatarUpload,
  messages,
  setMessages,
  clearAllStoredData,
  chatWidth,
  setChatWidth,
  chatHeight,
  setChatHeight
}) => {
  // Clear all messages
  const clearAllMessages = () => {
    setMessages([]);
  };

  // Reset to default settings
  const resetSettings = () => {
    setContactName('John Doe');
    setContactAvatar('');
    setMessages([]);
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Contact Settings</Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Box sx={{ position: 'relative', mr: 2 }}>
              <Avatar 
                src={contactAvatar || 'https://via.placeholder.com/80'} 
                alt={contactName}
                sx={{ width: 80, height: 80 }}
              />
              <input
                accept="image/*"
                type="file"
                id="avatar-upload"
                onChange={handleAvatarUpload}
                style={{ display: 'none' }}
              />
              <label htmlFor="avatar-upload">
                <IconButton 
                  component="span"
                  sx={{ 
                    position: 'absolute',
                    bottom: -5,
                    right: -5,
                    bgcolor: '#128C7E',
                    color: 'white',
                    '&:hover': {
                      bgcolor: '#075E54'
                    }
                  }}
                  size="small"
                >
                  <PhotoCameraIcon fontSize="small" />
                </IconButton>
              </label>
            </Box>
            
            <TextField
              label="Contact Name"
              variant="outlined"
              fullWidth
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </Box>
          
          <Typography variant="subtitle1" gutterBottom>
            Appearance Preview
          </Typography>
          
          <Paper 
            elevation={1}
            sx={{ 
              p: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: '#075E54',
              color: 'white',
              borderRadius: 1
            }}
          >
            <Avatar 
              src={contactAvatar || 'https://via.placeholder.com/40'} 
              alt={contactName}
              sx={{ mr: 1, width: 40, height: 40 }}
            />
            <Typography variant="body1">{contactName}</Typography>
          </Paper>
        </Paper>
      </Grid>
      
      <Grid item xs={12} md={6}>
        <Paper elevation={2} sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Conversation Settings</Typography>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Current conversation has {messages.length} messages
            </Typography>
            
            <Button 
              variant="outlined" 
              color="error"
              startIcon={<DeleteIcon />}
              onClick={clearAllMessages}
              disabled={messages.length === 0}
              sx={{ mb: 1 }}
            >
              Clear All Messages
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }} />
          
          <Typography variant="subtitle1" gutterBottom>
            Reset Settings
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="contained" 
              color="warning"
              onClick={resetSettings}
            >
              Reset to Default
            </Button>
            
            <Button 
              variant="contained" 
              color="error"
              onClick={clearAllStoredData}
            >
              Clear All Stored Data
            </Button>
          </Box>
          
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Tips
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Add messages from both sides using the chat controls
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Use emojis to make conversations more realistic
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Record animations by clicking the "Start Recording" button
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • The typing indicator will show before a message appears
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Adjust chat size in the Controls tab for better viewing
            </Typography>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default WhatsAppSettingsPanel;
